import { Injectable } from '@angular/core';
import { Auth } from 'aws-amplify';
import { CognitoUser } from '@aws-amplify/auth';
import { CTA } from './ctas/cta.model';

export type LS_RECORDER_TYPE = 'original' | 'snap';
export type LensBackgroundKey = '-1' | 'blur' | '0' | '1' | '2' | '3' | '4';
export type CustomBgMode = 'none' | 'blur' | 'image';

@Injectable({
  providedIn: 'root'
})
export class AppStorageService {

  readonly LS_LAST_AUDIO_DEVICE_KEY = 'LS_SnapService_Last_AudioDevice';
  readonly LS_LAST_VIDEO_DEVICE_KEY = 'LS_SnapService_Last_VideoDevice';
  readonly LS_LAST_CTA_URL_KEY = 'LS_CTA_Last_Display';
  readonly LS_LAST_CTA_DISPLAY_KEY = 'LS_CTA_Last_URL';
  readonly LS_DEFAULT_LENS_BEAUTY_VALUE = 'LS_DEFAULT_LENS_BEAUTY_VALUE';
  readonly LS_DEFAULT_LENS_BACKGROUND_VALUE = 'LS_DEFAULT_LENS_BACKGROUND_VALUE';
  readonly LS_HAS_VIEWED_NEW_CAPTURE_SETTINGS = 'LS_HAS_VIEWED_NEW_CAPTURE_SETTINGS';
  readonly LS_SHARE_USE_MERGE_TAGS = 'LS_SHARE_USE_MERGE_TAGS';
  readonly LS_TELEPROMPTER_SCRIPT_ID = 'LS_TELEPROMPTER_SCRIPT_ID';
  readonly LS_TELEPROMPTER_SPEED = 'LS_TELEPROMPTER_SPEED';
  readonly LS_TELEPROMPTER_FONT_SIZE = 'LS_TELEPROMPTER_FONT_SIZE';

  // Keys pulled in from elsewhere in the application prior to this service
  readonly TEMPLATE_STORAGE_KEY = 'lastTemplateType';
  readonly PING_KEY = 'LAST_PING';  

  // New keys as of this service
  readonly LS_LAST_RECORDER_KEY = 'LS_Video_Recorder';
  readonly LS_LAST_SELECTED_LENS_ID = 'LS_Last_Selected_Lens_Id';
  readonly LS_LAST_SELECTED_LENS_TEXT_MAP = 'LS_Last_Selected_Lens_Text_Map';
  readonly LS_CHROME_EXT_TEMPLATE = 'LS_Chrome_Ext_Template';
  readonly LS_CUSTOM_BG_MODE = 'LS_Custom_Bg_Mode';
  readonly LS_CUSTOM_BG_IMAGE = 'LS_Custom_Bg_Image';
  
  constructor() { }

  // User specific makes the key tied to the current authenticate user
  async getItem(key: string, userSpecific: boolean = false) {
    const user_id = await this.getCurrentUserName();
    const k = userSpecific ? `${user_id}.${key}` : key;
    try {      
      return localStorage.getItem(k);
    } catch (error) {
      console.error(`Error setting key ${k}`, error);
    }    
  }

  async setItem(key: string, value: string, userSpecific: boolean = false) {
    const user_id = await this.getCurrentUserName();
    const k = userSpecific ? `${user_id}.${key}` : key;
    try {
      return localStorage.setItem(k, value)
    } catch (error) {
      console.error(`Error saving item ${value} to key ${k}`, error);
    }    
  }

  // Returns the current user id or empty string
  async getCurrentUserName() {
     let user: CognitoUser = await Auth.currentAuthenticatedUser();
     if (!user) { return ''; }
     return user.getUsername();
  }

  /// Convenience functions

  // Recorder - User specific
  async getRecorder(): Promise<LS_RECORDER_TYPE> {
    return (await this.getItem(this.LS_LAST_RECORDER_KEY, true) ?? 'snap') as LS_RECORDER_TYPE;
  }

  async setRecorder(value: LS_RECORDER_TYPE): Promise<void> {
    return await this.setItem(this.LS_LAST_RECORDER_KEY, value, true);
  }

  async getLastSelectedLensId(): Promise<string | null> {
    const value = await this.getItem(this.LS_LAST_SELECTED_LENS_ID, true);
    if (!value || value.trim().length === 0) {
      return null;
    }
    return value;
  }

  async setLastSelectedLensId(value: string | null): Promise<void> {
    await this.setItem(this.LS_LAST_SELECTED_LENS_ID, value ?? '', true);
  }

  async getLastSelectedLensTextMap(): Promise<Record<string, string>> {
    const raw = await this.getItem(this.LS_LAST_SELECTED_LENS_TEXT_MAP, true);
    if (!raw) {
      return {};
    }

    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') {
        return parsed as Record<string, string>;
      }
    } catch {
      // Ignore malformed persisted JSON and return empty map.
    }

    return {};
  }

  async getLastSelectedLensTextForLensId(lensId: string): Promise<string | null> {
    const map = await this.getLastSelectedLensTextMap();
    const value = map[lensId];
    if (!value || value.trim().length === 0) {
      return null;
    }
    return value;
  }

  async setLastSelectedLensTextForLensId(lensId: string, text: string): Promise<void> {
    const map = await this.getLastSelectedLensTextMap();
    map[lensId] = text;
    await this.setItem(this.LS_LAST_SELECTED_LENS_TEXT_MAP, JSON.stringify(map), true);
  }


  // CTA's -- User specific
  async setLastCta(display: string, url: string): Promise<void> {
    await this.setItem(this.LS_LAST_CTA_DISPLAY_KEY, display, true);
    await this.setItem(this.LS_LAST_CTA_URL_KEY, url, true);
    return;
  }

  async getLastCta():Promise<Partial<CTA>> {
    const display = await this.getItem(this.LS_LAST_CTA_DISPLAY_KEY, true);
    const url = await this.getItem(this.LS_LAST_CTA_URL_KEY, true);

    return { cta_display: display, cta_url: url };
  }


  // Lenses

  /**
   * Store beauty intensity as a numeric string (e.g. "0.6").
   * A value of "0" means beauty is off.
   */
  async setLastBeautyValue(value: string): Promise<void> {
    await this.setItem(this.LS_DEFAULT_LENS_BEAUTY_VALUE, value, true);
    return;
  }

  /**
   * Returns the stored beauty intensity as a number (0–1).
   * Migrates legacy boolean-style values: '1' → 0.5, '0' → 0.
   */
  async getLastBeautyIntensity(): Promise<number> {
    const raw = await this.getItem(this.LS_DEFAULT_LENS_BEAUTY_VALUE, true) ?? '0';
    const num = parseFloat(raw);
    if (isNaN(num)) return 0;
    // Legacy migration: old code stored '1' for on, '0' for off.
    // Treat exactly 1 as the default 0.5 intensity.
    if (num === 1) return 0.5;
    return Math.max(0, Math.min(1, num));
  }

  async setLastBackgroundValue(value: LensBackgroundKey): Promise<void> {
    await this.setItem(this.LS_DEFAULT_LENS_BACKGROUND_VALUE, value, true);
    return;
  }

  async getLastBackgroundValue():Promise<LensBackgroundKey> {
    return (await this.getItem(this.LS_DEFAULT_LENS_BACKGROUND_VALUE, true) ?? '-1') as LensBackgroundKey; 
  }

  // New Settings
  async getHasViewedNewCaptureSettings(): Promise<boolean> {
    const viewed = await this.getItem(this.LS_HAS_VIEWED_NEW_CAPTURE_SETTINGS, true);
    return viewed === '1' ? true : false;
  }

  async setHasViewedNewCaptureSettings(value: boolean): Promise<void> {
    await this.setItem(this.LS_HAS_VIEWED_NEW_CAPTURE_SETTINGS, value ? '1' : '0', true);
  }

  // Merge tags
  async useMergeTags(): Promise<boolean> {
    const value = await this.getItem(this.LS_SHARE_USE_MERGE_TAGS, true);
    return value === '1' ? true : false;
  }

  async setUseMergeTags(value: boolean): Promise<void> {
    await this.setItem(this.LS_SHARE_USE_MERGE_TAGS, value ? '1' : '0', true);
  }

  // Teleprompter
  async getTeleprompterScriptId(): Promise<number | null> {
    const value = await this.getItem(this.LS_TELEPROMPTER_SCRIPT_ID, true);
    return value ? parseInt(value, 10) : null;
  }

  async setTeleprompterScriptId(value: number | null): Promise<void> {
    if (value === null) {
      await this.setItem(this.LS_TELEPROMPTER_SCRIPT_ID, '', true);
    } else {
      await this.setItem(this.LS_TELEPROMPTER_SCRIPT_ID, value.toString(), true);
    }
  }

  async getTeleprompterSpeed(): Promise<number> {
    const value = await this.getItem(this.LS_TELEPROMPTER_SPEED, true);
    return value ? parseInt(value, 10) : 150; // Default 150 WPM
  }

  async setTeleprompterSpeed(value: number): Promise<void> {
    await this.setItem(this.LS_TELEPROMPTER_SPEED, value.toString(), true);
  }

  async getTeleprompterFontSize(): Promise<number> {
    const value = await this.getItem(this.LS_TELEPROMPTER_FONT_SIZE, true);
    return value ? parseInt(value, 10) : 48; // Default 48px
  }

  async setTeleprompterFontSize(value: number): Promise<void> {
    await this.setItem(this.LS_TELEPROMPTER_FONT_SIZE, value.toString(), true);
  }

  // Chrome Extension Template
  async getChromeExtTemplate(): Promise<string | null> {
    return await this.getItem(this.LS_CHROME_EXT_TEMPLATE, true);
  }

  async setChromeExtTemplate(value: string): Promise<void> {
    await this.setItem(this.LS_CHROME_EXT_TEMPLATE, value, true);
  }

  // Custom Background
  async getCustomBgMode(): Promise<CustomBgMode> {
    return (await this.getItem(this.LS_CUSTOM_BG_MODE, true) ?? 'blur') as CustomBgMode;
  }

  async setCustomBgMode(value: CustomBgMode): Promise<void> {
    await this.setItem(this.LS_CUSTOM_BG_MODE, value, true);
  }

  async getCustomBgImage(): Promise<string | null> {
    return await this.getItem(this.LS_CUSTOM_BG_IMAGE, true) ?? null;
  }

  async setCustomBgImage(dataUrl: string): Promise<void> {
    await this.setItem(this.LS_CUSTOM_BG_IMAGE, dataUrl, true);
  }

  async clearCustomBgImage(): Promise<void> {
    await this.setItem(this.LS_CUSTOM_BG_IMAGE, '', true);
  }

}
